import { cloneDeep } from 'lodash';
import { ResponseBase } from '../../../api/response-base';
import { ApiPrintShopOrderDataPost } from '../../../api/sift/front/api-print-meta-shop-order-data';
import { AppThunk } from '../../../app/store';
import { PrintDataBase, PrintDataStructure, PrintDeliveryDataEntity, PrintItemDataEntity } from '../../../models/sift/json/print-model';
import { apiActions } from '../../api-slice';
import { localStorageActions } from '../../local-storage-slice';
import { printActions } from '../print-slice';
import { ApiReceptionSerialIdGet } from '../../../api/front/recepsion-serial-id/api-reception-serial-id';
import { UuidGenerator } from '../../../utilities/uuid-generator';
import { DateFormatter } from '../../../utilities/date-formatter';
import { ApiPrintMetaShopOrderGet, ApiPrintMetaShopOrderPost, MetaPrintShopOrderGetResponse } from '../../../api/sift/front/api-print-meta-shop-order';
import { ApiOrderFormCopy } from '../../../api/front/order-form/api-order-form';
import { dialogActions } from '../../../components/dialog/slice/dialog-slice';
import { ApiPrintLabOrderAlignment } from '../../../api/sift/front/labo-order-alignment';
import { MetaShopOrderGetResponse } from '../../../api/front/meta/api-meta-shop-order';
type BaseType = {
  kijshopCd: string,
  shopOrderId: string, 
}

type LabOrderErrorType = 'validation' | 'error' | 'center';

type CallbackType<T = undefined> = {
  success?: (param: T) => void,
  error?: () => void,
}

export const shopOrder = (param: BaseType) => ({
  create: (
    proxyMode: boolean,
    callback?: CallbackType<ReturnType<PrintDataStructure['exportJson']> & { structure: PrintDataStructure }>,
  ): AppThunk => async (dispatch, getState) => {
    const {
      kijshopCd,
      shopOrderId,
    } = param;
    const jsonModel = PrintDataStructure.create(kijshopCd, shopOrderId);
    const createInfo = async() => {
      const receptionSerialID = await new ApiReceptionSerialIdGet(kijshopCd).do()
        .then((res) => (res as ResponseBase<{ receptionSerialID: string }>)?.body?.data?.receptionSerialID || '')
        .catch(() => '');
      const orderGUID = UuidGenerator.create();
      const createDate = DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS.sss');
      const approval = !!getState().systemConfig.metaData.isNeedAgreement;
      return {
        receptionSerialID,
        orderGUID,
        createDate,
        approval,
        proxyMode,
      };
    }
    // インフォ
    const info = await createInfo();
    jsonModel.info = info;
    // 配送情報
    const delivery: PrintDeliveryDataEntity = {};
    const store = getState().storage.data.deliveryStore;
    const deliveryType = (getState().storage.data.deliveryType === 'store' || getState().storage.data.deliveryType === '') ? 'shop' : 'customer';
    if (getState().storage.data.deliveryType === '') dispatch(localStorageActions.setData(kijshopCd, 'deliveryType', 'store'));
    const masterShopIdList = getState().common.masterShopIdList;
    const deliveryStore = (masterShopIdList.find((v) => v === store)) ? store
      : (masterShopIdList.find((v) => v === kijshopCd)) ? kijshopCd : masterShopIdList[0];
    dispatch(localStorageActions.setData(kijshopCd, 'deliveryStore', deliveryStore));
    delivery.type = deliveryType;
    delivery.shopCode = shopOrderId.slice(0, 7);
    jsonModel.deliveryData = delivery;
    dispatch(printActions.setShopOrderData({ meta: jsonModel, image: [] }));
    const resultFileData = jsonModel.exportJson();
    dispatch(apiActions.run(
      new ApiPrintShopOrderDataPost({
        kijshopCd,
        shopOrderId,
        files: [
          {
            ...resultFileData,
            is_create: '1',
          },
        ],
      }),
      {
        onSuccess: (res: ResponseBase<{}>) => {
          if (Number(res.error.errorCode) !== 200)  {
            callback?.error?.();
          } else {
            callback?.success?.({ ...resultFileData, structure: jsonModel });
          }
        },
        onError: () => callback?.error?.(),
      },
      {
        ignoreSystemError: !callback?.error,
      }

    ));
  },
  update: (
    data: PrintDataStructure,
    callback?: CallbackType<ReturnType<PrintDataStructure['exportJson']>>,
  ): AppThunk => async(dispatch, getState) => {
    const {
      kijshopCd,
      shopOrderId,
    } = param;
    const resultFileData = data.exportJson();
    dispatch(printActions.setShopOrderData({ meta: cloneDeep(data), image: getState().print.printShopOrderData[shopOrderId]?.image ?? [] }));
    dispatch(apiActions.run(
      new ApiPrintShopOrderDataPost({
        kijshopCd,
        shopOrderId,
        files: [
          {
            ...resultFileData,
            is_create: '0',
          },
        ],
      }),
      {
        onSuccess: (res: ResponseBase<{}>) => {
          if (Number(res.error.errorCode) !== 200)  {
            callback?.error?.();
          } else {
            dispatch(printActions.setShopOrderData({
              image: getState().print.printShopOrderData[shopOrderId]?.image ?? [],
              meta: new PrintDataStructure(resultFileData.filename, {...data }),
            }));
            callback?.success?.(resultFileData);
          }
        },
        onError: () => callback?.error?.(),
      },
      {
        ignoreSystemError: !callback?.error,
      }
    ));
  },
  item: () => ({
    create: (
      itemData: PrintItemDataEntity,
      callback?: CallbackType<ReturnType<PrintDataStructure['exportJson']> & { structure: PrintDataStructure }>,
    ): AppThunk => async (dispatch, getState) => {
      const {
        kijshopCd,
        shopOrderId,
      } = param;
  
      dispatch(printActions.getShopOrderData(kijshopCd, shopOrderId,
        {
          onSuccess: (res) => {
            if (!res.meta.item) {
              res.meta.item = {};
            }
            const processData = {
              date: DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS', '/'),
              machineId: getState().storage.machineId,
            }
            itemData.process = { start: processData };
            res.meta.item[itemData.id] = itemData;
            const json = res.meta.exportJson();
            dispatch(apiActions.run(
              new ApiPrintShopOrderDataPost({
                kijshopCd,
                shopOrderId,
                files: [
                  {
                    is_create: '0',
                    ...json,
                  },
                ],
              }),
              {
                onSuccess: (successRes: ResponseBase<{}>) => {
                  if (Number(successRes.error.errorCode) !== 200) {
                    callback?.error?.();
                  } else {
                    callback?.success?.({ ...json, structure: res.meta });
                  }
                },
                onError: () => {
                  callback?.error?.();
                }
              }  ,
              {
                ignoreSystemError: !callback?.error,
              }
            ))
          }
        }
      ));
    },
    delete: (
      id: string,
      callback?: CallbackType<PrintDataStructure>,
    ): AppThunk => async(dispatch, getState) => {
      const {
        kijshopCd,
        shopOrderId,
      } = param;
      dispatch(printActions.getShopOrderData(kijshopCd, shopOrderId,
        {
          onSuccess: (target) => {
            if (!target) throw new Error('存在しない商品です。');
            const clone = cloneDeep(target.meta);
            const currentItem = clone.item?.[id];
            console.log(clone.item);
            if (!currentItem) throw new Error('存在しないアイテムです。');
            delete clone.item?.[id];
            if (clone.item?.[id]) {
              throw new Error('アイテムの削除に失敗しました。');
            }
            console.log(clone);
            dispatch(printActions.shopOrder(param).update(clone, {
              success: () => callback?.success?.(clone),
            }));

          }
        },
      ));
    },
    delivery: (
      data: PrintDeliveryDataEntity | undefined,
      callback?: CallbackType<PrintDataStructure>,
    ): AppThunk => async(dispatch, getState) => {
      const {
        kijshopCd,
        shopOrderId,
      } = param;

      dispatch(printActions.getShopOrderData(kijshopCd, shopOrderId,
        {
          onSuccess: (res) => {
            if (!res.meta.item) {
              res.meta.item = {};
            }
            res.meta.deliveryData = data;
            const json = res.meta.exportJson();
            dispatch(apiActions.run(
              new ApiPrintShopOrderDataPost({
                kijshopCd,
                shopOrderId,
                files: [
                  {
                    is_create: '0',
                    ...json,
                  },
                ],
              }),
              {
                onSuccess: (saveRes: ResponseBase<{}>) => {
                  if (Number(saveRes.error.errorCode) !== 200) {
                    callback?.error?.();
                  } else {
                    callback?.success?.(res.meta);
                    dispatch(printActions.setShopOrderData({
                      image: [],
                      ...getState().print.printShopOrderData[shopOrderId],
                      meta: res.meta,                      
                    }))
                  }
                },
                onError: () => {
                  callback?.error?.();
                }
              }  ,
              {
                ignoreSystemError: !callback?.error,
              }
            ))
          }
        }
      ));
    }
  }),
  labOrder: (
    data: {
      callback?: CallbackType<ReturnType<PrintDataStructure['exportJson']>> & {
        customError?: (param: { code: LabOrderErrorType, message: string[] }) => void,
      },
      isRetry?: boolean,
    }
  ): AppThunk => async (dispatch, getState) => {
    const {
      kijshopCd,
      shopOrderId,
    } = param;
    const {
      callback,
      isRetry,
    } = data;
    const getJson = () => new Promise<PrintDataStructure>((resolve) => {
      dispatch(printActions.getShopOrderData(kijshopCd, shopOrderId,
        {
          onSuccess: (res) => {
            if (!res.meta) {
              callback?.error?.();
              callback?.customError?.({ code: 'error', message: ['注文情報の取得に失敗しました。'] });
            }
            resolve(res.meta);
          },
          onError: () => {
            callback?.customError?.({ code: 'error', message: ['注文情報の取得に失敗しました。'] });
            callback?.error?.();
          }
        }
      ));
    });
    const getMeta = () => new Promise<MetaPrintShopOrderGetResponse>((resolve) => {
      dispatch(printActions.getOrderMeta(kijshopCd, shopOrderId, {
        onSuccess: (res) => {
          resolve(res);
        },
        onError: () => {
          callback?.customError?.({ code: 'error', message: ['注文情報の取得に失敗しました。'] });
          callback?.error?.();
        },
      }));
    });
    const updateMeta = (rollback: boolean = false) => new Promise<void>((resolve) => {
      dispatch(apiActions.run(
        new ApiPrintMetaShopOrderGet({
          kijshopCd,
          shopOrderId,
        }),
        {
          onSuccess: (res: ResponseBase<MetaPrintShopOrderGetResponse>) => {
            if (!res.body.data || (Number(res.error?.errorCode) !== 200)) {
              callback?.error?.();
              callback?.customError?.({ code: 'error', message: ['注文情報の取得に失敗しました。'] });
            } else {
              const data = res.body.data;
              if (!data) {
                callback?.error?.();
                callback?.customError?.({ code: 'error', message: ['注文情報の取得に失敗しました。'] });
                  return;
              } else {
                dispatch(apiActions.run(new ApiPrintMetaShopOrderPost({
                  ...res.body.data,
                  kijshopCd,
                  shopOrderId,
                  lastUpdateDate: DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS', '/'),
                  status: 'ラボ発注中',
                  orderDate: DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS'),
                  orderUser: getState().storage.machineId,
                  orderCompleteDate: rollback 
                    ? DateFormatter.date2str(new Date(), 'YYYYMMDD_HHmmSS', '/')
                    : null,
                }), {
                  onSuccess: () => resolve(),
                  onError: (e) => {
                    callback?.error?.();
                    callback?.customError?.({ code: 'error', message: ['注文情報の更新に失敗しました。'] });        
                  },
                }, { ignoreSystemError: true }));
              };
            }
          },
          onError: () => {
            callback?.error?.();
            callback?.customError?.({ code: 'error', message: ['注文情報の取得に失敗しました。'] });
          }
        }
      ));
    });

    // ラボ発注処理
    const labOrder = () => new Promise<void>((resolve, reject) => {
      dispatch(apiActions.run(
        new ApiPrintLabOrderAlignment({ kijshopCd, shopOrderId, debug: 0, isReRequest: data.isRetry ? 1 : 0 }),
        {
          onSuccess: (res: ResponseBase<MetaShopOrderGetResponse>) => {
            if (res?.body?.data && (Number(res?.error.errorCode) === 200)) {
              resolve();
            } else {
              if ((Number(res?.error.errorCode) === 406)) {
                callback?.error?.();
                callback?.customError?.({ code: 'center', message: [] });
                return;
              }
              reject('failed lab order');
            }
          },
          onError: (e) => {
            if ((Number(e?.error.errorCode) === 406)) {
              callback?.error?.();
              callback?.customError?.({ code: 'center', message: [] });
              return;
            }
            updateMeta(true).finally(() => {
              callback?.error?.();
              callback?.customError?.({ code: 'error', message: ['ラボ発注処理に失敗しました。'] });

              });
            }
        },
        { ignoreSystemError: true },
      ));
    });
    

    const model = await getJson();
    const meta = await getMeta();
    if (!meta.lastName) {
      callback?.customError?.({ code: 'validation', message: ['お客様名が入力されていません'] });
      return;
    }
    if (model.deliveryData?.orderFormGUID) {
      const flag = await new Promise<boolean>((resolve) => {
        new ApiOrderFormCopy({
          kijshopCd,
          shopOrderId,
          orderformId: model.deliveryData?.orderFormGUID ?? '',
        })
        .do()
        .then((res) => String((res as ResponseBase<any>)?.error?.errorCode) === '200' ? resolve(true) : resolve(false))
        .catch(() => resolve(false));
      });
      if (!flag) {
        dispatch(dialogActions.pushMessage({
          title: '確認',
          message: [
            '「発送帳票」で指定したPDFが見つかりません',
            '【注文概要画面】の「配送情報登録」にてPDFを指定してください',
          ],
          buttons: [{
            label: 'OK',
            callback: () => {
              dispatch(dialogActions.pop());
            },
          }],
        }));
        callback?.customError?.({ code: 'validation', message:  [] });
        return;
      }
    }
    await updateMeta();
    await labOrder();
    dispatch(printActions.setShopOrderData({
      meta: model,
      image: [],
    }));
    callback?.success?.(model.exportJson());
  }
});